/**
 * WaitingRoomPanel - Waiting Room Tab Content
 *
 * Compact table showing patients currently in the waiting room with:
 * - Arrival Time (from firstAppearInWaiting)
 * - Patient name and DOB
 * - Client and SiteCode
 * - Triage category (trCAT)
 * - Wait Time with color coding
 * - Clock status (Running=red/waiting, Stopped=green/attended)
 * - Stop Reason
 * - Elapsed time
 * - SLA progress bars with color coding
 */

import { useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import { Table, Typography, Progress, Space, Empty } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { useUnifiedDashboardStore } from '../../store';
import { parseAsLocalTime } from '../../utils/timeUtils';
import useTableResize from '../../hooks/useTableResize';
import ResizeHandle from './ResizeHandle';

const { Text } = Typography;

const WaitingRoomPanel = () => {
  const { unifiedRecords, getWaitingRoomRecords } = useUnifiedDashboardStore();

  const {
    customTableHeight,
    tableWrapperRef,
    handleResizeMouseDown,
    resetHeight,
  } = useTableResize({ defaultHeight: 320 });

  // Get waiting room records - depends on unifiedRecords to trigger recompute
  const waitingRecords = useMemo(
    () => getWaitingRoomRecords(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unifiedRecords]
  );

  // Build unique filter options
  const buildFilters = useCallback(
    (key, accessor) => {
      const values = new Set();
      waitingRecords.forEach((record) => {
        const val = accessor ? accessor(record) : record[key];
        if (val && typeof val === 'string' && val.trim()) {
          values.add(val.trim());
        }
      });
      return Array.from(values)
        .sort()
        .map((v) => ({ text: v, value: v }));
    },
    [waitingRecords]
  );

  const siteFilters = useMemo(
    () => buildFilters('site', (r) => r.site?.code),
    [buildFilters]
  );

  // Build client filters
  const clientFilters = useMemo(
    () => buildFilters('client', (r) => r.site?.client),
    [buildFilters]
  );

  // Build clock status filters
  const clockStatusFilters = useMemo(
    () => buildFilters('clockStatus', (r) => r.waitingRoom?.clockStatus),
    [buildFilters]
  );

  // Get SLA progress bar color
  const getSlaProgressColor = (percentage) => {
    if (!percentage) return '#d9d9d9';
    if (percentage >= 100) return '#ff4d4f';
    if (percentage >= 80) return '#fa8c16';
    if (percentage >= 50) return '#d4b106'; // Darker yellow for better readability
    return '#52c41a';
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        title: 'Arrival Time',
        key: 'arrivalTime',
        width: 90,
        render: (_, record) => {
          const arrival = record.waitingRoom?.firstAppearInWaiting;
          if (!arrival) return '-';
          // Use parseAsLocalTime to handle Z suffix correctly
          const parsed = parseAsLocalTime(arrival);
          return parsed ? parsed.format('HH:mm') : '-';
        },
        sorter: (a, b) => {
          // Use parseAsLocalTime to handle Z suffix correctly
          const parsedA = parseAsLocalTime(a.waitingRoom?.firstAppearInWaiting);
          const parsedB = parseAsLocalTime(b.waitingRoom?.firstAppearInWaiting);
          const arrA = parsedA?.valueOf() || 0;
          const arrB = parsedB?.valueOf() || 0;
          return arrA - arrB;
        },
        defaultSortOrder: 'descend',
      },
      {
        title: 'Patient',
        key: 'patient',
        width: 140,
        render: (_, record) => {
          const name =
            `${record.patient?.firstName || ''} ${record.patient?.lastName || ''}`.trim();
          return (
            <Text ellipsis style={{ maxWidth: 130 }}>
              {name || 'Unknown'}
            </Text>
          );
        },
      },
      {
        title: 'DOB',
        key: 'dob',
        width: 100,
        render: (_, record) => {
          const dob = record.patient?.dob;
          if (!dob) return '-';
          const parsed = dayjs(dob);
          return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '-';
        },
        sorter: (a, b) => {
          const dobA = a.patient?.dob ? dayjs(a.patient.dob).valueOf() : 0;
          const dobB = b.patient?.dob ? dayjs(b.patient.dob).valueOf() : 0;
          return dobA - dobB;
        },
      },
      {
        title: 'client',
        key: 'client',
        width: 100,
        ellipsis: true,
        filters: clientFilters,
        onFilter: (value, record) => record.site?.client === value,
        render: (_, record) => (
          <Text ellipsis style={{ maxWidth: 90 }}>
            {record.site?.client || '-'}
          </Text>
        ),
      },
      {
        title: 'SiteCode',
        key: 'siteCode',
        width: 140,
        ellipsis: true,
        filters: siteFilters,
        onFilter: (value, record) =>
          (record.site?.code || '').toLowerCase().includes(value.toLowerCase()),
        render: (_, record) => (
          <Text ellipsis style={{ maxWidth: 130 }}>
            {record.site?.code || '-'}
          </Text>
        ),
      },
      {
        title: 'trCAT',
        key: 'trCAT',
        width: 70,
        align: 'center',
        render: (_, record) => {
          const triage = record.appointment?.triageCategory;
          if (!triage || triage === '0' || triage === 0) return '-';
          return triage;
        },
        sorter: (a, b) =>
          (a.appointment?.triageCategoryNum || 99) -
          (b.appointment?.triageCategoryNum || 99),
      },
      {
        title: 'Wait (min)',
        key: 'waitTime',
        width: 80,
        align: 'center',
        render: (_, record) => {
          // Don't show wait time if task is in wrapping status
          if (record.call?.status === 'wrapping') return '-';

          const wait = record.waitingRoom?.waitTime;
          if (!Number.isFinite(wait)) return '-';
          // Color code based on wait time
          const bgColor =
            wait >= 30 ? '#ffe7e7' : wait >= 15 ? '#fff7e6' : '#e6ffe6';
          return (
            <span
              style={{
                backgroundColor: bgColor,
                padding: '2px 8px',
                borderRadius: 4,
                fontWeight: 500,
              }}
            >
              {wait}
            </span>
          );
        },
        sorter: (a, b) =>
          (a.waitingRoom?.waitTime || 0) - (b.waitingRoom?.waitTime || 0),
      },
      {
        title: 'Clock status',
        key: 'clockStatus',
        width: 90,
        filters: clockStatusFilters,
        onFilter: (value, record) => record.waitingRoom?.clockStatus === value,
        render: (_, record) => {
          const status = record.waitingRoom?.clockStatus;
          if (!status) return '-';
          // Stopped is good (being attended), Running means still waiting
          const color = status === 'Stopped' ? 'green' : 'red';
          return <Text style={{ color }}>{status}</Text>;
        },
      },
      {
        title: 'Stop Reason',
        key: 'stopReason',
        width: 160,
        ellipsis: true,
        render: (_, record) => {
          const reason = record.waitingRoom?.stopReason;
          if (!reason) return '-';
          return (
            <Text ellipsis style={{ maxWidth: 150 }}>
              {reason}
            </Text>
          );
        },
      },
      {
        title: 'Elapsed time (min)',
        key: 'elapsedTime',
        width: 90,
        align: 'center',
        render: (_, record) => {
          const elapsed = record.waitingRoom?.elapsedTime;
          if (!Number.isFinite(elapsed)) return '-';
          return elapsed;
        },
        sorter: (a, b) =>
          (a.waitingRoom?.elapsedTime || 0) - (b.waitingRoom?.elapsedTime || 0),
      },
      {
        title: 'SLA',
        key: 'sla',
        width: 130,
        render: (_, record) => {
          const percentage = record.slaStatus?.percentage;
          const waitTime = record.waitingRoom?.waitTime;
          const slaMinutes = record.waitingRoom?.slaMinutes;

          if (!Number.isFinite(percentage) || !Number.isFinite(waitTime)) {
            return <Text type="secondary">-</Text>;
          }

          const remaining = slaMinutes - waitTime;
          const isOver = remaining < 0;
          const absRemaining = Math.abs(remaining);

          // Format time strings
          const formatTime = (mins) => {
            if (mins >= 60) {
              return `${Math.floor(mins / 60)}h${mins % 60}m`;
            }
            return `${mins}m`;
          };

          const remainingStr = isOver
            ? `+${formatTime(absRemaining)}`
            : formatTime(absRemaining);

          return (
            <div style={{ lineHeight: 1.2 }}>
              <Progress
                percent={Math.min(percentage, 100)}
                size="small"
                strokeColor={getSlaProgressColor(percentage)}
                style={{ width: '100%', marginBottom: 2 }}
                showInfo={false}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 10,
                }}
              >
                <span style={{ color: '#8c8c8c' }}>{formatTime(waitTime)}</span>
                <span style={{ color: '#d9d9d9' }}>|</span>
                <span style={{ color: isOver ? '#ff4d4f' : '#52c41a' }}>
                  {remainingStr}
                </span>
              </div>
            </div>
          );
        },
        sorter: (a, b) =>
          (b.slaStatus?.percentage || 0) - (a.slaStatus?.percentage || 0),
      },
    ],
    [siteFilters, clientFilters, clockStatusFilters]
  );

  return (
    <div style={{ padding: '12px 0' }}>
      {/* Summary Stats */}
      <Space size={16} wrap style={{ marginBottom: 12 }}>
        <Text type="secondary">
          {waitingRecords.length} patient
          {waitingRecords.length !== 1 ? 's' : ''} in waiting room
        </Text>
        <Text style={{ color: '#d48806' }}>
          <ClockCircleOutlined /> {waitingRecords.length} waiting
        </Text>
      </Space>

      {/* Table */}
      {waitingRecords.length === 0 ? (
        <Empty
          description="No patients in waiting room"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ padding: '24px 0' }}
        />
      ) : (
        <div ref={tableWrapperRef}>
          <Table
            size="small"
            dataSource={waitingRecords}
            columns={columns}
            rowKey="id"
            pagination={false}
            scroll={{ y: customTableHeight || 320, x: 1400 }}
          />
          <ResizeHandle
            onMouseDown={handleResizeMouseDown}
            onDoubleClick={resetHeight}
          />
        </div>
      )}
    </div>
  );
};

export default WaitingRoomPanel;
